import { eventBus } from "../observability/eventBus";
import { makeEvent, type SessionEvent, type SessionSummary } from "../observability/eventSchema";
import {
  getMetrics,
  type SttMetric,
  type StructureMetric,
  type TtsMetric,
} from "../observability/metrics";
import type { SessionUploader } from "./sessionUploader";

export type FlushReason = "submit" | "pagehide" | "visibility_hidden_beacon" | "error";

export type SessionEventBufferAttachArgs = {
  userId: string;
  sessionId: string;
  startedAt: string;
};

/** spec: docs/superpowers/specs/2026-07-11-r2-log-expansion-design.md §3.4 buffer 上限 (event 数)。上限到達で dropOldest が発火する。 */
const BUFFER_CAP = 1000;

/** spec: docs/superpowers/specs/2026-07-11-r2-log-expansion-design.md §7.2 beacon 48 KB cap */
const BEACON_CAP_BYTES = 48 * 1024;

/** localStorage key for last beacon state (Task 3, spec: docs/superpowers/specs/2026-07-11-r2-log-expansion-design.md §3.5) */
const LAST_BEACON_KEY = "audio-form:lastBeacon";

/**
 * spec: docs/superpowers/specs/2026-07-25-events-in-session-durability-design.md §2。
 * foreground snapshot の既定間隔 (時間 arm) / 意味的 event 件数 threshold (件数 arm)。
 * どちらも constructor options で上書きできる。
 */
const SNAPSHOT_INTERVAL_MS = 10000;
const SNAPSHOT_EVENT_THRESHOLD = 25;

/**
 * spec: docs/superpowers/specs/2026-07-11-r2-log-expansion-design.md §3.4 drop 対象外の event type。
 * dropOldest 実行時にこれらは skip され、他に drop 候補が無ければ cap 超過を許容する。
 */
const CRITICAL_TYPES: ReadonlySet<string> = new Set([
  "session.started",
  "session.ended",
  "mic.acquire_failed",
  "wss.error",
  "bargein.detected",
  "bargein.tts_cancelled",
  // 読み上げを保持したあと「回答ではなかった」と判定して再開した記録
  // (spec 2026-07-28 §6)。相方の bargein.detected が critical なので、こちらだけ
  // 落とすと「保持した回数」に対する「誤って止めた回数」の比が壊れる。
  // しかも壊れ方に偏りがある — イベントが最も多いセッション = 最も回り込みが
  // 酷いセッションでだけ誤爆率が過小評価される。
  "bargein.resumed",
  // transcript.json 廃止 (2026-07-18) 後、events.jsonl の scribe.final が発話テキストの正。
  // >48KB beacon の trim / cap 超過の dropOldest で落とさない (X6、spec 2026-07-25 §3.4)。
  "scribe.final",
  // 送ったのに確定文が返らなかった記録 (A2、2026-08-18)。**落とすと壊れ方に偏りが出る** —
  // `bargein.resumed` と同じ理屈で、イベントが最も多いセッション = **最も発話が消えて
  // いるセッション**でだけ消失が過小評価される。相方の `scribe.final` が critical なので、
  // 「返った」だけが残って「返らなかった」が落ちると消失率そのものが測れない。
  // 件数は発話数で自然に上限が付き (1 発話に最大 1 件)、payload も 2 フィールドなので、
  // 無制限な type を足したときの「critical だけで 48KB を超える」事故にはならない。
  "scribe.final_timeout",
  // spec 2026-07-29 D9。エラーが真っ先に捨てられては本末転倒。
  // ただし trimForBeacon は critical だけになると break して 48KB 超のまま送り、
  // sendBeacon が false を返して最終バッチが丸ごと消える。ここに無制限な type を
  // 足すと事故になるため、ERROR_TYPE_CAPS と payload の切り詰めで
  // 積み増しを最大 ~7KB に抑えてある (48KB に対して 15% 程度)。
  "app.error",
  "app.crash",
  // storage.upload_failed は入れない (D10) — 「R2 に届かなかった記録を R2 に残す」
  // 矛盾があり、価値は localStorage 退避側にある。
  //
  // spec §6.8 (2026-08-15)。どちらも**離脱セッションでこそ読みたい**ものなので、
  // critical でないと beacon の 48KB trim と dropOldest で真っ先に捨てられる
  // (= まさに知りたいセッションから消える)。積み増しは下の cap で抑えてある。
  // 「その人は設問を聞いたのか」に答えるための記録。
  "tts.spoken",
  // 「タブを切り替えただけの人」を離脱から救うための記録。
  "page.visible",
]);

/**
 * spec 2026-07-29 §3.2。type ごとの 1 session あたりの上限。
 * 上限に達したこと自体は記録しない — 「打ち止めイベント」を足すと
 * それ自体が次の 1 件になって同じ問題を再生産する。
 * 上限値ちょうどで止まっている事実が、上限到達の証拠になる。
 */
const ERROR_TYPE_CAPS: ReadonlyMap<string, number> = new Map([
  ["app.error", 10],
  ["app.crash", 5],
  ["storage.upload_failed", 10],
  // spec §6.8 (2026-08-15)。**エラー系専用の表ではない** — 判定は型名だけを見る汎用の
  // 件数上限で、CRITICAL_TYPES に入れた型の積み増しを抑えるためにここを使う
  // (critical だけになると trimForBeacon が 48KB 超のまま送り、sendBeacon が false を
  // 返して最終バッチが丸ごと消えるため)。実測 15 件 / 完走なので 40 は十分な余裕。
  ["tts.spoken", 40],
  ["page.visible", 10],
]);

/**
 * spec 2026-07-29 §3.3。snapshot の件数 arm から除外する type。
 * - scribe.partial: 発話中 ~10/s で PUT storm を招く (既存)
 * - storage.upload_failed: 「失敗の記録が次の送信を呼ぶ」ループを断つ (新規)。
 *   これが無いと 保存失敗 → emit → snapshot 発火 → また失敗 で回り続ける。
 */
const SNAPSHOT_TRIGGER_EXCLUDED: ReadonlySet<string> = new Set([
  "scribe.partial",
  "storage.upload_failed",
]);

type BeaconReason = "pagehide" | "visibility_hidden_beacon";

interface WindowLike {
  addEventListener: (type: string, handler: () => void) => void;
  removeEventListener: (type: string, handler: () => void) => void;
  document: { visibilityState: string };
  navigator: { sendBeacon: (url: string, data: Blob) => boolean };
  localStorage: {
    getItem: (key: string) => string | null;
    setItem: (key: string, value: string) => void;
    removeItem: (key: string) => void;
  };
}

interface StorageOnly {
  localStorage: {
    getItem: (key: string) => string | null;
    setItem: (key: string, value: string) => void;
    removeItem: (key: string) => void;
  };
}

/**
 * spec: docs/superpowers/specs/2026-07-25-events-in-session-durability-design.md §3 / X5。
 * `enableSnapshots` は foreground snapshot + beacon re-arm のハードニング全体を gate する
 * (既定 false)。options を渡さない既存呼び出しは今日の挙動 (interval 無し / beaconSent 恒久
 * latch) を保つ。singleton (`sessionEventBufferInstance.ts`) だけが opt-in する。
 */
export interface SessionEventBufferOptions {
  enableSnapshots?: boolean;
  snapshotIntervalMs?: number;
  snapshotEventThreshold?: number;
}

export class SessionEventBuffer {
  private events: SessionEvent[] = [];
  private unsubscribe: (() => void) | null = null;
  private attached: SessionEventBufferAttachArgs | null = null;
  private utteranceCounter = 0;
  private droppedByType: Record<string, number> = {};
  private beaconDroppedCount = 0;
  private beaconSent = false;
  // foreground snapshot (enableSnapshots) 用の状態。
  private snapshotTimer: ReturnType<typeof setInterval> | null = null;
  private newSinceSnapshot = 0; // 全 event (時間 arm の skip-on-zero ガード用)
  private meaningfulSinceSnapshot = 0; // scribe.partial 除外 (件数 arm 用)
  /**
   * spec 2026-07-29 §3.2。type ごとの emit 回数 (ERROR_TYPE_CAPS の判定用)。
   * カウンタを emit 側ではなくここに置くのは、emit 側が複数ファイルに散っていて
   * リセット契機がばらけるため。attach() が session 単位のリセット点になる。
   */
  private errorTypeCounts = new Map<string, number>();
  /**
   * タブが hidden になった時刻。visible で `page.visible.awayMs` を出すために持つ。
   * null は「hidden を経ていない」= 復帰ではない (初期化直後の visibilitychange など)。
   */
  private hiddenAtMs: number | null = null;
  /** 復帰した回数 (`page.visible.index`)。1 始まり。 */
  private visibleCount = 0;
  private readonly snapshotsEnabled: boolean;
  private readonly snapshotIntervalMs: number;
  private readonly snapshotEventThreshold: number;

  constructor(
    private readonly uploader: SessionUploader,
    private readonly windowLike: WindowLike = window as unknown as WindowLike,
    options: SessionEventBufferOptions = {}
  ) {
    this.snapshotsEnabled = options.enableSnapshots ?? false;
    this.snapshotIntervalMs = options.snapshotIntervalMs ?? SNAPSHOT_INTERVAL_MS;
    this.snapshotEventThreshold = options.snapshotEventThreshold ?? SNAPSHOT_EVENT_THRESHOLD;
  }

  attach(args: SessionEventBufferAttachArgs): void {
    if (this.attached) {
      this.detach();
    }
    this.attached = args;
    this.events = [];
    this.utteranceCounter = 0;
    this.droppedByType = {};
    this.beaconDroppedCount = 0;
    this.beaconSent = false;
    this.newSinceSnapshot = 0;
    this.meaningfulSinceSnapshot = 0;
    this.errorTypeCounts.clear();
    this.hiddenAtMs = null;
    this.visibleCount = 0;
    this.unsubscribe = eventBus.subscribe((ev) => this.onEvent(ev));
    this.windowLike.addEventListener("pagehide", this.onPageHide);
    this.windowLike.addEventListener("visibilitychange", this.onVisibilityChange);
    this.startSnapshotTimer();
  }

  detach(): void {
    // Freeze beacon so a pagehide after submit->detach cannot double-send.
    this.beaconSent = true;
    this.stopSnapshotTimer();
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.windowLike.removeEventListener("pagehide", this.onPageHide);
    this.windowLike.removeEventListener("visibilitychange", this.onVisibilityChange);
    this.attached = null;
  }

  /**
   * submit 経路の同期 seal (detach とは別)。unload capture (beacon + snapshot interval +
   * window listener) だけを止め、`attached` と eventBus subscription は残す。したがって
   * この後の `flush("submit")` は正常保存でき、`await finalizeManifest` 中に発生した遅延
   * event も buffer される。re-arm で beaconSent が per-visible-episode に緩むため、submit の
   * await 中に background すると abandoned beacon が完了 submit と競合しうる。それを閉じる。
   * spec: docs/superpowers/specs/2026-07-25-events-in-session-durability-design.md §3.3。
   */
  stopUnloadCapture(): void {
    this.beaconSent = true;
    this.stopSnapshotTimer();
    this.windowLike.removeEventListener("pagehide", this.onPageHide);
    this.windowLike.removeEventListener("visibilitychange", this.onVisibilityChange);
  }

  flush(reason: FlushReason): void {
    if (!this.attached) return;
    const { sessionId } = this.attached;

    // submit path: events + summary を uploader queue へ乗せる (spec: docs/superpowers/specs/2026-07-11-r2-log-expansion-design.md §7.2)。
    // pagehide / visibility_hidden_beacon は Task 3 で追加する window listener +
    // navigator.sendBeacon 経路で送るため、この flush() は uploader を呼ばない。
    // error (I2): handleStartOver など、ページが生きたまま前 session を破棄して
    // 新 session に attach() し直す経路から呼ばれる。attach() 内部の detach() は
    // buffer を silent discard するだけなので、その前に events をベストエフォートで
    // upload する。summary は含めない (「summary の存在 = completed session」という
    // spec: docs/superpowers/specs/2026-07-11-r2-log-expansion-design.md §D4 の invariant を維持するため)。beacon 経路と異なりここでは終端の
    // session.ended は付与しない (この時点ではまだ session は「中断」であって
    // pagehide/visibility_hidden_beacon のような unload ではないため)。後段解析は
    // 該当 session の summary.json 不在から abandoned と判別できる。
    if (reason === "submit") {
      // **summary を events より先に積む** (A5-3、docs/next-pilot-readiness.md)。
      // `SessionUploader` は 1 本の直列キューで 1 オブジェクトに約 1.1 秒かかるので、
      // 最後尾は送信直後にタブを閉じられると落ちる。2026-08-05 のパイロットでは
      // 完走したのに `summary.json` が無いセッションが 1 件あり、R2 のタイムスタンプが
      // 「events.jsonl まで書けて summary が来ていない」形だった。
      //
      // どちらを先にするかは非対称:
      //   - `events.jsonl` は in-session snapshot が 10 秒 / 25 件ごとに書いているので、
      //     この flush が落ちても**失うのは tail だけ**
      //   - `summary.json` は他のどこにも書かれず、落ちると session 単位の集計が丸ごと
      //     消える。しかも**不在が「完走ではない」の材料に読まれる** (§4.3)
      //   - 大きさも逆で、events は数百 KB になりうるが summary は固定長に近い
      // (2026-08-02 の「saveAnswers を発話 WAV より先に積む」と同じ判断。)
      this.uploader.enqueueSessionSummary({
        sessionId,
        summary: this.aggregateSummary(),
      });
      this.uploader.enqueueEvents({
        sessionId,
        events: [...this.events],
      });
    } else if (reason === "error") {
      if (this.events.length > 0) {
        this.uploader.enqueueEvents({
          sessionId,
          events: [...this.events],
        });
      }
    }
    this.events = [];
  }

  private onEvent(ev: SessionEvent): void {
    // spec 2026-07-29 §3.2: エラー系 type は 1 session あたりの件数を絞る。
    // 上限に達したら黙って捨てる (打ち止めイベントは足さない — それ自体が
    // 次の 1 件になって同じ問題を再生産する)。
    const cap = ERROR_TYPE_CAPS.get(ev.type);
    if (cap !== undefined) {
      const seen = this.errorTypeCounts.get(ev.type) ?? 0;
      if (seen >= cap) return;
      this.errorTypeCounts.set(ev.type, seen + 1);
    }

    if (this.events.length >= BUFFER_CAP) {
      this.dropOldest();
    }

    // 最終レビュー指摘 (Finding 3): Preflight の氏名復唱 (recognizeSingleUtterance) は
    // 本編と同じ VadController/StreamingScribeClient を使い回すため、本編開始
    // (session.started, ChatScreen が survey ロード時に emit) より前に
    // vad.speech_start が発火し得る (氏名の試行回数分)。ここで採番を 0 に
    // リセットし、本編最初の発話が常に utteranceIndex=0 になるようにする
    // (?preflight=skip は session.started が attach 直後に発火するため実質 no-op)。
    if (ev.type === "session.started") {
      this.utteranceCounter = 0;
    }

    // spec: docs/superpowers/specs/2026-07-11-r2-log-expansion-design.md §7.2: vad.speech_start に utteranceIndex を自動採番。
    // 元 event の payload を mutate せず shallow clone で新しい event を作る
    // (eventBus は他 subscriber にも同 event を配るため副作用を避ける)。
    // 早期 return を避け if/else にして、push 後に必ず snapshot カウントを 1 回だけ通す
    // (vad.speech_start も件数 arm に確実に効かせるための単一 exit)。
    if (ev.type === "vad.speech_start") {
      this.events.push({
        ...ev,
        payload: {
          ...ev.payload,
          utteranceIndex: this.utteranceCounter++,
        },
      });
    } else {
      this.events.push(ev);
    }

    this.countForSnapshot(ev);
  }

  // spec: docs/superpowers/specs/2026-07-25-events-in-session-durability-design.md §3.1。
  // newSinceSnapshot は全 event (時間 arm の skip-on-zero 用)、meaningfulSinceSnapshot は
  // scribe.partial を除外 (件数 arm 用。発話中 ~10/s の partial で full-buffer PUT storm /
  // 共有 per-IP rate limit の圧迫を避ける)。snapshot 無効時は完全 no-op。
  private countForSnapshot(ev: SessionEvent): void {
    if (!this.snapshotsEnabled) return;
    this.newSinceSnapshot++;
    if (!SNAPSHOT_TRIGGER_EXCLUDED.has(ev.type)) {
      this.meaningfulSinceSnapshot++;
      if (this.meaningfulSinceSnapshot >= this.snapshotEventThreshold) {
        this.flushSnapshot();
      }
    }
  }

  /**
   * Foreground の定期 snapshot。buffer 全件を既存の PUT /events (idempotent 全上書き) で
   * 保存する。`flush("submit")` と違い buffer を drain せず summary も出さない。
   * spec: docs/superpowers/specs/2026-07-25-events-in-session-durability-design.md §3.1。
   */
  private flushSnapshot(): void {
    // beaconSent ガードが correctness critical: beacon 発火後は interval / 件数 の
    // どちらの経路からも撃たない。撃つと server が full-overwrite するため、beacon が
    // 書いた終端 session.ended{abandoned} (synthetic、this.events には無い) を消してしまう。
    if (!this.attached || this.beaconSent || this.newSinceSnapshot === 0) return;
    this.uploader.enqueueEvents({
      sessionId: this.attached.sessionId,
      events: [...this.events],
    });
    this.newSinceSnapshot = 0;
    this.meaningfulSinceSnapshot = 0;
  }

  // タイマーは単一所有者。clear-then-set で二重 interval (attach 二重 / hidden⇄visible flap)
  // を防ぐ。グローバル setInterval を使う (jsdom + vi.useFakeTimers が fake する)。
  private startSnapshotTimer(): void {
    if (!this.snapshotsEnabled) return;
    this.stopSnapshotTimer();
    this.snapshotTimer = setInterval(() => this.flushSnapshot(), this.snapshotIntervalMs);
  }

  private stopSnapshotTimer(): void {
    if (this.snapshotTimer !== null) {
      clearInterval(this.snapshotTimer);
      this.snapshotTimer = null;
    }
  }

  /**
   * spec: docs/superpowers/specs/2026-07-11-r2-log-expansion-design.md §3.4 drop policy:
   * 1. scribe.partial を古い順に 1 件 drop
   * 2. 無ければ CRITICAL_TYPES 以外の event を古い順に 1 件 drop
   * 3. どちらも該当しなければ (buffer が critical のみ) drop せず cap 超過を許容
   */
  private dropOldest(): void {
    for (let i = 0; i < this.events.length; i++) {
      if (this.events[i].type === "scribe.partial") {
        this.recordDrop(this.events[i].type);
        this.events.splice(i, 1);
        return;
      }
    }
    for (let i = 0; i < this.events.length; i++) {
      if (!CRITICAL_TYPES.has(this.events[i].type)) {
        this.recordDrop(this.events[i].type);
        this.events.splice(i, 1);
        return;
      }
    }
    // critical のみ → drop しない
  }

  private recordDrop(type: string): void {
    this.droppedByType[type] = (this.droppedByType[type] ?? 0) + 1;
  }

  // ---- Task 3: beacon path (spec: docs/superpowers/specs/2026-07-11-r2-log-expansion-design.md §7.2 / §3.4 / §3.5) ----

  private onPageHide = (): void => {
    this.sendBeacon("pagehide");
    // beacon 後は snapshot が終端 session.ended{abandoned} を上書きしないよう interval を止める
    // (flushSnapshot の beaconSent ガードと二重の防御。snapshot 無効時は no-op)。
    this.stopSnapshotTimer();
  };

  private onVisibilityChange = (): void => {
    if (this.windowLike.document.visibilityState === "hidden") {
      this.hiddenAtMs = Date.now();
      this.sendBeacon("visibility_hidden_beacon");
      this.stopSnapshotTimer();
    } else {
      // **戻ってきたことを記録する。** #79 (A5-2) が「タブを隠すのは離脱ではない」として
      // beacon の合成 session.ended を pagehide 限定にしたため、**hidden 側の痕跡は
      // R2 に 1 バイトも残らない**。したがってこれがタブ往復の唯一の記録になる。
      // (離脱の誤カウント自体は #79 が根元で直したので、こちらの狙いは
      // 「離れていた回数と時間を後から数えられること」に移っている。)
      //
      // **`this.events` に積むのが要点** (onEvent 経由なので cap と drop policy にも乗る)。
      // `/events` は PUT/POST とも全上書きなので、送信配列にしか積まない記録は
      // 戻ってから snapshot が 1 回走ると痕跡ごと消える。
      //
      // `page.hidden` は作らない — hidden の時刻は `timestamp − awayMs` で復元できる。
      if (this.attached && this.hiddenAtMs !== null) {
        const awayMs = Math.max(0, Date.now() - this.hiddenAtMs);
        this.hiddenAtMs = null;
        this.visibleCount += 1;
        this.onEvent(
          makeEvent("page.visible", this.attached.sessionId, {
            awayMs,
            // 1 始まりの復帰回数。個々が drop されても最大値で総回数が読める。
            index: this.visibleCount,
          })
        );
      }
      if (this.snapshotsEnabled) {
        // visible: re-arm (#2 tail を閉じる)。戻ってきたら真の離脱で再度 beacon を送れるよう
        // beaconSent を解除し、snapshot も再開する。フラグ off では既存挙動 (恒久 latch) を保つ。
        this.beaconSent = false;
        this.startSnapshotTimer(); // clear-then-set で二重 interval を防ぐ
      }
    }
  };

  private sendBeacon(reason: BeaconReason): void {
    if (this.beaconSent || !this.attached) return;
    this.beaconSent = true;
    const attached = this.attached;

    // session.ended を末尾に append (abandoned 復元用、spec: docs/superpowers/specs/2026-07-11-r2-log-expansion-design.md §4.3)。
    //
    // **タブを隠すのは離脱ではない** (A5-2、docs/next-pilot-readiness.md)。
    // 2026-08-05 のパイロットで「最初の画面だけで終わった」10 件のうち 3 件が
    // この経路 (876ms / 6.9s / 22s) で、離脱していないのに離脱として数えられていた。
    //
    // 落としても離脱の検出は落ちない — **判定はすでに「summary.json 不在 +
    // manifest.status」に正準化されている** (spec §4.3) ので、この synthetic な終端は
    // 判定の材料ではなく、解析で「ここで終わった」と読まれるだけの誤情報になっていた。
    // 送ること自体はやめない: この経路の目的は tail を落とさないことで、そちらは残る。
    //
    // 本当の離脱は `pagehide` が示すので、そちらは従来どおり終端を付ける。
    const eventsToSend: SessionEvent[] =
      reason === "pagehide"
        ? [...this.events, this.buildSessionEndedForBeacon(reason, attached)]
        : [...this.events];

    // 48 KB cap 内に収まるまで drop policy を適用
    const trimmed = this.trimForBeacon(eventsToSend);

    // trim 完了後に beaconDroppedCount を session.ended.payload に反映
    const endIdx = trimmed.findIndex((e) => e.type === "session.ended");
    if (endIdx >= 0) {
      (trimmed[endIdx].payload as Record<string, unknown>).beaconDropped = this.beaconDroppedCount;
    }

    const jsonl = trimmed.map((e) => JSON.stringify(e)).join("\n");
    const blob = new Blob([jsonl], { type: "application/x-ndjson" });
    const ok = this.windowLike.navigator.sendBeacon(
      `/api/sessions/${attached.sessionId}/events?userId=${encodeURIComponent(attached.userId)}`,
      blob
    );

    if (!ok) {
      console.warn("[sessionEventBuffer] navigator.sendBeacon returned false");
    }

    // 次 session 起動時に previousBeaconFailed を検知するため localStorage に残す
    try {
      this.windowLike.localStorage.setItem(
        LAST_BEACON_KEY,
        JSON.stringify({
          sessionId: attached.sessionId,
          sentAt: new Date().toISOString(),
          sendBeaconOk: ok,
        })
      );
    } catch {
      /* quota / private mode / disabled storage は無視 */
    }
  }

  private trimForBeacon(events: SessionEvent[]): SessionEvent[] {
    const result = [...events];
    let jsonl = result.map((e) => JSON.stringify(e)).join("\n");
    while (new Blob([jsonl]).size > BEACON_CAP_BYTES && result.length > 1) {
      // 1. scribe.partial を古い順に drop
      let dropIdx = result.findIndex((e) => e.type === "scribe.partial");
      // 2. なければ非 critical を古い順に drop
      if (dropIdx < 0) {
        dropIdx = result.findIndex((e) => !CRITICAL_TYPES.has(e.type));
      }
      // 3. critical のみになったら drop しない (cap 超過を許容)
      if (dropIdx < 0) break;
      result.splice(dropIdx, 1);
      this.beaconDroppedCount++;
      jsonl = result.map((e) => JSON.stringify(e)).join("\n");
    }
    return result;
  }

  private buildSessionEndedForBeacon(
    reason: BeaconReason,
    attached: SessionEventBufferAttachArgs
  ): SessionEvent {
    const now = new Date();
    const startedAtMs = new Date(attached.startedAt).getTime();
    const durationMs = Math.max(0, Number.isFinite(startedAtMs) ? now.getTime() - startedAtMs : 0);
    return makeEvent("session.ended", attached.sessionId, {
      endedAt: now.toISOString(),
      status: "abandoned",
      reason,
      // beacon 発火時は turn 集計を持たないので 0 で送る (spec: docs/superpowers/specs/2026-07-11-r2-log-expansion-design.md §6.3)
      turns: 0,
      answered: 0,
      skipped: 0,
      durationMs,
      beaconDropped: 0, // trim 後に上書き
    });
  }

  /**
   * spec: docs/superpowers/specs/2026-07-11-r2-log-expansion-design.md §5.3 SessionSummary を構築する (Task 9)。
   * stt / structure / tts は getMetrics() rolling buffer snapshot をこの session の
   * window (recordedAt in [startedAt, generatedAt(=now)]) で絞って集計する。
   * interactions / session.turnCount は buffer 内 event (this.events) から集計する。
   * overflow は Task 2 の droppedByType と Task 3 の beaconDroppedCount をそのまま反映する。
   */
  aggregateSummary(): SessionSummary {
    if (!this.attached) {
      throw new Error("[sessionEventBuffer] aggregateSummary called before attach()");
    }
    const { userId, sessionId, startedAt } = this.attached;
    const generatedAt = new Date().toISOString();
    const startedAtMs = Date.parse(startedAt);
    const endedAtMs = Date.parse(generatedAt);

    // 最終レビュー指摘 (Finding 1/2): 本編 (survey) は session.started event
    // (ChatScreen が survey ロード時に emit) から始まる。App が session bootstrap
    // (buffer attach) を Preflight mount 前に前倒ししたため、それより前の buffer 内容
    // (Preflight のスピーカーテスト・氏名復唱由来の event/metric) は survey 活動では
    // ない。session.started が見つからない場合 (wizard 離脱等で本編未到達の
    // abandoned session) は attach() 時刻に fallback する (従来どおりの挙動)。
    const surveyStartedEvent = this.events.find((e) => e.type === "session.started");
    const surveyStartMs = surveyStartedEvent
      ? Date.parse(surveyStartedEvent.timestamp)
      : startedAtMs;
    const surveyStartedAt = surveyStartedEvent?.timestamp ?? startedAt;
    const durationMs = Math.max(0, endedAtMs - surveyStartMs);

    // --- metrics snapshot (session window で絞る) ---
    // I3: handleStartOver 等で 2 session が短時間で切り替わると、前 session の async
    // 完了 metric (STT/structure/TTS) が新 session の time window に recordedAt ベースで
    // 落ちて誤って集計される可能性があった (cross-session contamination)。
    // sessionId が付与されている metric は sessionId 一致を優先して判定し、
    // 付与されていない metric (旧データ/未対応 producer) は従来通り time-window で
    // fallback 判定する (backward compat)。
    // Finding 1: surveyStartMs より前の metric (Preflight のスピーカーテスト合成音声 /
    // 氏名復唱 TTS など) はここでまとめて除外する。sessionId の一致判定より先に
    // 見るので、tagged/untagged どちらの metric にも一律に効く。
    const all = getMetrics().filter((m) => {
      if (m.recordedAt < surveyStartMs) return false;
      if (m.sessionId !== undefined) {
        return m.sessionId === sessionId;
      }
      return m.recordedAt >= startedAtMs && m.recordedAt <= endedAtMs;
    });
    const stt = all.filter((m): m is SttMetric => m.kind === "stt");
    const structure = all.filter((m): m is StructureMetric => m.kind === "structure");
    const tts = all.filter((m): m is TtsMetric => m.kind === "tts");

    // --- event 由来の count / distinct turnId ---
    const turnIds = new Set<string>();
    let bargeinCount = 0;
    let silenceLv0 = 0;
    let silenceLv1 = 0;
    let silenceLv2 = 0;
    let silenceRecoveryCount = 0;
    let wssErrorCount = 0;
    let autoAdvanceCount = 0;
    let micAcquireFailedCount = 0;
    // spec 2026-07-29 D11。いずれも type ごとの上限 (ERROR_TYPE_CAPS) で
    // 頭打ちになるため、上限値ちょうどの場合は「その回数以上」と読む。
    let appErrorCount = 0;
    let crashCount = 0;
    let uploadFailedCount = 0;

    for (const ev of this.events) {
      if (ev.turnId) turnIds.add(ev.turnId);
      switch (ev.type) {
        case "bargein.tts_cancelled":
          bargeinCount += 1;
          break;
        case "silence.level_up": {
          const level = ev.payload.level;
          if (level === 0) silenceLv0 += 1;
          else if (level === 1) silenceLv1 += 1;
          else if (level === 2) silenceLv2 += 1;
          break;
        }
        case "silence.recovery":
          silenceRecoveryCount += 1;
          break;
        case "wss.error":
          // 最終レビュー指摘 (Finding 3): session.started より前の wss.error は
          // Preflight 氏名復唱の WSS 接続 (recognizeSingleUtterance) 由来。
          // events.jsonl には残すが survey 集計 (wssErrorCount) からは除外する。
          if (Date.parse(ev.timestamp) >= surveyStartMs) {
            wssErrorCount += 1;
          }
          break;
        case "autoadvance.fired":
          autoAdvanceCount += 1;
          break;
        case "mic.acquire_failed":
          micAcquireFailedCount += 1;
          break;
        case "app.error":
          appErrorCount += 1;
          break;
        case "app.crash":
          crashCount += 1;
          break;
        case "storage.upload_failed":
          uploadFailedCount += 1;
          break;
        default:
          break;
      }
    }

    return {
      schemaVersion: 1,
      sessionId,
      userId,
      generatedAt,
      stt: {
        count: stt.length,
        meanRoundTripMs: mean(stt.map((m) => m.roundTripMs)),
        p95RoundTripMs: p95OrMax(stt.map((m) => m.roundTripMs)),
        totalCharsProcessed: sumBy(stt, (m) => m.chars),
        totalCostUsd: sumBy(stt, (m) => m.estimatedCostUsd),
        model: firstModel(stt),
      },
      structure: {
        count: structure.length,
        meanRoundTripMs: mean(structure.map((m) => m.roundTripMs)),
        p95RoundTripMs: p95OrMax(structure.map((m) => m.roundTripMs)),
        totalInputTokens: sumBy(structure, (m) => m.inputTokens),
        totalOutputTokens: sumBy(structure, (m) => m.outputTokens),
        totalCostUsd: sumBy(structure, (m) => m.estimatedCostUsd),
        model: firstModel(structure),
      },
      tts: {
        count: tts.length,
        meanRoundTripMs: mean(tts.map((m) => m.roundTripMs)),
        p95RoundTripMs: p95OrMax(tts.map((m) => m.roundTripMs)),
        totalCharsSynthesized: sumBy(tts, (m) => m.chars),
        totalCostUsd: sumBy(tts, (m) => m.estimatedCostUsd),
        model: firstModel(tts),
      },
      session: {
        turnCount: turnIds.size,
        durationMs,
        startedAt: surveyStartedAt,
        endedAt: generatedAt,
        // aggregateSummary は flush("submit") の completed 経路からのみ呼ばれる (spec: docs/superpowers/specs/2026-07-11-r2-log-expansion-design.md §7.2)
        status: "completed",
      },
      interactions: {
        bargeinCount,
        silenceLevelUpCounts: {
          level0: silenceLv0,
          level1: silenceLv1,
          level2: silenceLv2,
        },
        silenceRecoveryCount,
        wssErrorCount,
        autoAdvanceCount,
        micAcquireFailedCount,
        appErrorCount,
        crashCount,
        uploadFailedCount,
      },
      overflow: {
        droppedCount: Object.values(this.droppedByType).reduce((a, b) => a + b, 0),
        droppedByType: { ...this.droppedByType },
        beaconDroppedCount: this.beaconDroppedCount,
      },
    };
  }
}

function mean(values: readonly number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

// spec: docs/superpowers/specs/2026-07-11-r2-log-expansion-design.md §5.3: count<20 のとき p95 は max で代替 (PoC scope、正規 percentile は 20 件以上から)
function p95OrMax(values: readonly number[]): number {
  if (values.length === 0) return 0;
  if (values.length < 20) {
    return values.reduce((max, v) => (v > max ? v : max), -Infinity);
  }
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.floor((sorted.length - 1) * 0.95));
  return sorted[idx];
}

function sumBy<T>(items: readonly T[], fn: (item: T) => number): number {
  return items.reduce((acc, item) => acc + fn(item), 0);
}

// continuous conversation は 1 kind 1 model 前提 (同一 kind 内で model が変わらない) のため先頭 metric の model を使う。
// metric が 0 件のときは schema (z.string().min(1)) を満たす "none" を返す。
// 例外 (2026-07-26 の固定文言 TTS 事前生成 以降): tts はキャッシュヒット ("prerendered") と API 合成
// ("eleven_flash_v2_5") が 1 session 内で混在するため、先頭 metric の model は代表値ではない。事前チェックの
// 固定文言が最初に読み上げられる都合で tts.model はほぼ常に "prerendered" になり、meanRoundTripMs /
// p95RoundTripMs も二峰の混成値になる。個別 metric の model は events.jsonl には残らない (tts.* の event type は
// 無い) — getMetrics() の localStorage rolling buffer にしか無く、そこから切り分けるしかない。
// spec: docs/superpowers/specs/2026-07-11-r2-log-expansion-design.md §5.3 の注記。
function firstModel(items: readonly { readonly model: string }[]): string {
  return items.length > 0 ? items[0].model : "none";
}

/**
 * localStorage["audio-form:lastBeacon"] を consume-once で読み、
 * 前 session の navigator.sendBeacon が false を返していれば true を返す。
 * 呼び出し後は entry を削除するので、次 session で beacon が起きなかった場合も
 * 再度 previousBeaconFailed=true を報告しない。spec: docs/superpowers/specs/2026-07-11-r2-log-expansion-design.md §3.5 retrofit。
 */
export function readPreviousBeaconFailed(windowLike: StorageOnly = window): boolean {
  try {
    const raw = windowLike.localStorage.getItem(LAST_BEACON_KEY);
    if (!raw) return false;
    windowLike.localStorage.removeItem(LAST_BEACON_KEY);
    const parsed = JSON.parse(raw) as { sendBeaconOk?: unknown };
    return parsed.sendBeaconOk === false;
  } catch {
    return false;
  }
}
